import { type CustomTooltipProps } from "ag-grid-react"

import { Card, CardContent, CardHeader, CardTitle } from "~/components/reusable/Card"
import { filterDefined } from "~/utils/tsUtils"

import HeaderTooltip from "./HeaderTooltip"
import { formatSkuId } from "~/utils/helpers"
import { sortSizesComparator } from "~/utils/sizeSplit"

interface ListTooltipProps extends CustomTooltipProps {
  title: string
  isSKU?: boolean
}

/** Tagged value for a cell tooltip set from a cell renderer via `CustomAgGridTooltip`. */
export type UniqueListTooltipValue = {
  tooltipType: "UniqueList"
  values: string[]
}

const UniqueListTooltip = (props: ListTooltipProps) => {
  if (props.location === "header") return <HeaderTooltip {...props} />

  const values = props.value?.tooltipType === "UniqueList" ? props.value.values : props.value
  const elements = filterDefined(values).filter((value: any) => value !== "") as string[]
  if (elements.length <= 1) return null
  if (props.location === "cell") {
    if (props.title === "Sizes") elements.sort(sortSizesComparator)

    return <UniqueListTooltipContent values={elements} title={props.title} isSKU={props.isSKU} />
  }
}

/**
 * Simple list content for displaying unique values in a tooltip.
 *
 * USE THIS FOR:
 * - `valueTooltip` prop on `InformationRow` component
 * - Any `Tooltip.body` prop where the Tooltip already provides container styling
 *
 * DO NOT USE FOR:
 * - ag-grid custom tooltips (use `UniqueListTooltipContent` instead)
 *
 * @param values - Array of string or number values to display
 * @param title - Optional title shown above the list
 * @param suffix - Optional suffix appended to each value (e.g., "days", "units", "DKK")
 * @param isSKU - If true, formats values using formatSkuId
 */
const UniqueValueListContent = ({
  values,
  title,
  suffix,
  isSKU = false,
}: {
  values: (string | number)[]
  title?: string
  suffix?: string
  isSKU?: boolean
}) => {
  if (values.length <= 1) return null
  return (
    <div className="text-left max-h-[250px] overflow-y-auto">
      {title && <div className="font-medium mb-1">{title}</div>}
      {values.map((value) => (
        <div key={value}>
          - {isSKU ? formatSkuId(String(value)) : value}
          {suffix && ` ${suffix}`}
        </div>
      ))}
    </div>
  )
}

/**
 * Card-wrapped list content for ag-grid custom tooltips.
 *
 * USE THIS FOR:
 * - ag-grid `tooltipComponent` (CustomTooltipProps) where tooltips render standalone
 *
 * DO NOT USE FOR:
 * - `valueTooltip` on `InformationRow` (causes double-wrapping)
 * - `Tooltip.body` prop (use `UniqueValueListContent` instead)
 */
const UniqueListTooltipContent = ({
  values,
  title,
  isSKU = false,
}: {
  values: string[]
  title: string
  isSKU?: boolean
}) => {
  return (
    <div className="bg-base w-[300px] mr-6 z-10">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="max-h-[600px] overflow-y-auto">
          {values.map((value) => (
            <div key={value}>- {isSKU ? formatSkuId(value) : value}</div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

export { UniqueListTooltip, UniqueListTooltipContent, UniqueValueListContent }
